import { ChangeDetectionStrategy, Component, computed, input, output, signal, ViewEncapsulation } from "@angular/core";
import { Doc } from "./s25.document.const";
import { ReportBuilderCell } from "./s25.document.report.builder.table.component";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { DocumentService } from "../../../services/document.service";
import { DocumentUtil } from "./s25.document.util";
import { InferSignal } from "../../../pojo/Util";
import { Bind } from "../../../decorators/bind.decorator";
import { S25Util } from "../../../util/s25-util";
import { CssProperty } from "../../s25-styles/s25.css.rule.component";

@Component({
    standalone: false,
    selector: "s25-ng-document-report-builder-cell-editor",
    template: `
        <div>
            <label>Label</label>
            <input
                class="c-input"
                [class.invalid]="!!errors.label"
                type="text"
                [(ngModel)]="cell().label"
                (ngModelChange)="onChange()"
            />
            @if (errors.label) {
                <span class="error ngRed">{{ errors.label }}</span>
            }
        </div>
        <div>
            <label>Type</label>
            <select [(ngModel)]="cell().variable().type" (ngModelChange)="onVariableTypeChange()">
                <option [value]="'variable'">Variable</option>
                <option [value]="'function'">Function</option>
                <option [value]="'subreport'">Subreport</option>
            </select>
        </div>
        <div>
            <label>Data</label>
            <s25-ng-bpe-variable
                [state]="cell().variable().state"
                [type]="templateVariableKey()"
                [codeMode]="false"
                [documentType]="documentType()"
                [hasPost]="false"
                [hasFormatExample]="false"
                [prefix]="false"
                [wrap]="false"
                [hasNoneItem]="true"
                [itemFilter]="addSubreports"
                [placeholder]="templateVariablePlaceholder()"
                [scrollIntoView]="false"
                (variableChange)="onVariableChange(cell())"
            />
            @if (cell().variable().state.var === "concat") {
                <span>Combine variables and text in a single cell</span>
                <div class="arguments">
                    @for (arg of cell().variable().arguments || []; track $index) {
                        <div class="argument">
                            @if (arg.type === "text") {
                                <div>
                                    <input
                                        type="text"
                                        class="c-input"
                                        [class.invalid]="!!errors.arguments[$index]"
                                        [(ngModel)]="arg.text"
                                        (ngModelChange)="updateDisplay(cell())"
                                    />
                                    @if (!!errors.arguments[$index]) {
                                        <span class="error ngRed">{{ errors.arguments[$index] }}</span>
                                    }
                                </div>
                            } @else if (arg.type === "variable") {
                                <s25-ng-bpe-variable
                                    [state]="arg.state"
                                    [type]="documentType()"
                                    [codeMode]="false"
                                    [documentType]="documentType()"
                                    [hasPost]="false"
                                    [hasFormatExample]="false"
                                    [prefix]="false"
                                    [wrap]="false"
                                    [hasNoneItem]="true"
                                    [scrollIntoView]="false"
                                    (variableChange)="updateDisplay(cell())"
                                />
                            }
                            <button class="deleteArgument dot" (click)="deleteArgument(cell(), $index)">
                                <s25-ng-icon [type]="'minus'" [label]="'Delete'" />
                            </button>
                        </div>
                    }
                </div>
                <div class="buttons">
                    <s25-ng-button [type]="'outline'" (click)="addArgument(cell(), 'text')"> Add Text </s25-ng-button>
                    <s25-ng-button [type]="'outline'" (click)="addArgument(cell(), 'variable')">
                        Add Variable
                    </s25-ng-button>
                </div>
            }
        </div>
        <div>
            <label>Styles</label>
            <s25-ng-css-rule
                [(styles)]="cell().css"
                [allowedProperties]="Doc.supportedCssProperties"
                [validateProperty]="validateCssProperty"
                [(valid)]="cssValid"
                (validChange)="validate()"
                (stylesChange)="changed.emit()"
            />
        </div>
    `,
    styles: `
        :host {
            --border-width: 1px;
            --border-color: #0002;
            --border-radius: 3px;
            --resize-handle-width: 5px;
            --control-bg: #f2f2f2;
            --green: #60a725;
            --red: #d62000;
            --red-alpha: color-mix(in lch, var(--red), #fff0 85%);
            --cell-highlight: color-mix(in lch, var(--cn-blue), #fff0 85%);
            --argument-color: color-mix(in hsl, var(--cn-blue), #fff0 92%);
        }

        ::ng-deep body.nm-party--on s25-ng-document-report-builder-cell-editor {
            --argument-color: color-mix(in hsl, var(--cn-blue), #fff0 70%);
        }

        :host {
            display: grid;
            gap: 0.5rem;
            grid-template-columns: 100%;
        }

        label {
            font-weight: bold;
        }

        :host > div {
            display: grid;
            gap: 0.1rem;
            grid-template-columns: 100%;
        }

        .buttons {
            display: flex;
            justify-content: space-evenly;
        }

        .argument {
            padding: 0.5rem;
            border-radius: var(--border-radius);
            margin-bottom: 0.5rem;
            background: var(--argument-color);
            display: flex;
            justify-content: space-between;
            gap: 0.5rem;
        }

        .argument > :first-child {
            flex-grow: 1;
        }

        .argument > :first-child input {
            width: 100%;
        }

        .deleteArgument {
            background-color: var(--red);
            border-radius: 50%;
            aspect-ratio: 1;
            font-size: 0.6em;
            align-self: center;
            position: relative;
            height: 2em;
            border: 0;
            cursor: pointer;

            s25-ng-icon {
                top: 50%;
                left: 50%;
                translate: -50% -50%;
                font-size: 0.6rem;
                color: white;
                position: absolute;
            }
        }

        select {
            align-items: center;
            background-color: var(--cn-blue);
            border-radius: 3px;
            color: white !important;
            border: 0;
            padding-block: 0.2em;
        }

        ::ng-deep ngb-popover-window:has(s25-ng-document-report-builder-cell-editor) {
            --bs-popover-max-width: 350px !important;
            width: 350px;
        }

        ::ng-deep s25-ng-document-report-builder-cell-editor s25-ng-bpe-variable .selectedVarLabel {
            display: none;
        }

        ::ng-deep s25-ng-document-report-builder-cell-editor s25-dropdown-paginated .select2-choice {
            padding-left: 0 !important;
        }

        .invalid.invalid {
            border-color: var(--conflict-color) !important;
            background: color-mix(in hsl, var(--conflict-color), transparent 90%);
        }

        .error {
            font-size: 0.9em;
            display: block;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentReportBuilderCellEditorComponent {
    documentType = input.required<Doc.ReportBuilderTypeId>();
    cell = input.required<ReportBuilderCell>();

    changed = output<void>();
    errors: { label?: string; arguments: string[] } = { arguments: [] };
    cssValid = signal(true);

    protected readonly Doc = Doc;

    templateVariableKey = computed(() => {
        const type = this.cell().variable().type();
        if (type === "variable") return this.documentType();
        if (type === "function") return "reportBuilderFunctions";
        if (type === "subreport") return "none";
    });
    templateVariablePlaceholder = computed(() => S25Util.firstCharToUpper(this.cell().variable().type()));

    @Bind
    async addSubreports(items: DropDownItem[]) {
        if (this.cell().variable().type() !== "subreport") return items;
        if (Doc.type[this.documentType()].type === "subreport") return items; // No nested subreports

        const subreports = await DocumentService.getDocumentList(null, "subreport");
        const mapped = subreports.map((subreport) => ({
            val: `subreport.${subreport.itemId}`,
            txt: subreport.itemName,
            grp: "Subreports",
        }));
        return [{ isGroup: true, groupName: "Subreports" }, ...mapped, ...items];
    }

    onVariableTypeChange() {
        this.cell().variable().state.variable = undefined;
    }

    onVariableChange(cell: ReportBuilderCell) {
        if (cell.variable().type() === "subreport" && cell.variable().state.variable) {
            cell.variable().subreportId = Number(cell.variable().state.variable.val.split(".")[1]);
        }
        this.updateDisplay(cell);
    }

    updateDisplay(cell: ReportBuilderCell) {
        const value = DocumentUtil.getReportBuilderCellDisplayValue(cell);
        cell.display.set(value);
        this.onChange();
    }

    addArgument(
        cell: ReportBuilderCell,
        type: InferSignal<ReportBuilderCell["variable"]>["arguments"][number]["type"],
    ) {
        if (type === "text") cell.variable().arguments.push({ type, text: "" });
        if (type === "variable") cell.variable().arguments.push({ type, state: { options: {} } });

        this.updateDisplay(cell);
    }

    deleteArgument(cell: ReportBuilderCell, index: number) {
        cell.variable().arguments.splice(index, 1);
        this.updateDisplay(cell);
    }

    onChange() {
        this.validate();
        this.changed.emit();
    }

    validate() {
        this.cell().valid.set(true);
        this.errors = { arguments: [] };

        if (/[\^]/.test(this.cell().label())) {
            this.errors.label = "Label cannot contain ^";
            this.cell().valid.set(false);
        }

        if (this.cell().variable().type() === "function" && !!this.cell().variable().state.variable) {
            const args = this.cell().variable().arguments;
            this.errors.arguments = args.map((arg) => {
                if (arg.type === "text" && /[\^,)]/.test(arg.text)) return "Function text cannot contain: ,^)";
            });
            if (this.errors.arguments.some((err) => !!err)) this.cell().valid.set(false);
        }

        if (!this.cssValid()) this.cell().valid.set(false);
    }

    validateCssProperty(property: CssProperty) {
        if (!!property.property && /[\^]/.test(property.value)) return "Styles cannot contain ^";
    }
}
